import {
  AfterViewInit,
  ChangeDetectorRef,
  ComponentRef,
  Directive,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { DateTimeService, UIService } from '@services';
import * as Highcharts from 'highcharts';
import { BehaviorSubject, Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import {
  TrendingChartType,
  TrendingData,
  TrendingItem,
} from 'src/app/@store/trending/trending.model';

import { cloneDeep, isEqual } from 'lodash-es';

import { ActiveErrorsStore, DevicesStore, InstallationDetailsStore, TrendingStore } from '@store';
import { ViToastComponent } from '@vi-iot/ui';

import { DateRange } from '@angular/material/datepicker';
import { MatSnackBarRef } from '@angular/material/snack-bar';
import { getEnumValues } from '@common';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { TRENDING_UNITS_ADAPTER_IT } from '../../../../operational-data-common/services/units-adapter/units-adapter.module';
import { UnitsAdapter } from '../../../../operational-data-common/services/units-adapter/units.adapter';
import { DAYS_SHORT, MONTHS_SHORT, WEEKDAYS } from '../../../../shared/common/enums';
import { TrendingChartLegendComponent } from './trending-chart-legend/trending-chart-legend.component';
import {
  SeriesWithGroupedData,
  TrendingCategory,
  initBaseTrendingChartOptions,
  resizeTrendingChart,
  showChartTooltip,
  updateTrendingChartExtremes,
} from './trending-chart.helpers';
import { trendingProperties } from '@vi-iot/client/index';

@Directive()
export abstract class BaseTrendingChartComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() trends: TrendingItem[];
  @Input() name: string;
  @Input() trendingCategories: TrendingCategory[];
  @Input() isHasCategories: boolean;

  highCharts: typeof Highcharts = Highcharts;
  chart: Highcharts.Chart;
  chartOptions: Highcharts.Options;
  selectedProperties = new FormControl<string[] | undefined>(undefined);
  trendsFiltered: TrendingItem[];
  toastRef: MatSnackBarRef<ViToastComponent>;

  dataGroupingLabel: Highcharts.SVGElement | undefined;
  dataGrouping: boolean;
  protected displayTooltip$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  protected maxSelectedProperties = 15;
  protected customLegendElement: ComponentRef<TrendingChartLegendComponent>;

  protected chartNameKey: string;
  protected installationTimezone: string;
  protected ngUnsubscribe$: Subject<void> = new Subject<void>();
  protected initChartOptions: Highcharts.Options = {
    ...cloneDeep(initBaseTrendingChartOptions),
    chart: {
      ...initBaseTrendingChartOptions.chart,
      events: {
        render: () => {
          this.checkSeriesDataGroupingAndSetLabel();
        },
      },
    },
    xAxis: {
      ...initBaseTrendingChartOptions.xAxis,
      events: {
        afterSetExtremes: (event) => {
          this.onAfterSetExtremes(event);
          resizeTrendingChart(this.chartOptions, this.chart);
        },
      },
    },
    plotOptions: {
      series: {
        visible: true,
        events: {
          // happening before state update
          legendItemClick: (e) => {
            const isUncheck = e.target.visible;

            let visibleSeries = e.target.chart.series.filter((entry) => {
              // weird series "Naviator 1" appearing in some cases
              return !entry.name.includes('Navigator') && !!entry.visible;
            });

            if (isUncheck && visibleSeries.length - 1 < 1) {
              return false;
            } else if (!isUncheck && visibleSeries.length + 1 > 15) {
              return false;
            }

            this.onSelectChartProperty(e.target.userOptions.id);

            return true;
          },
        },
        dataGrouping: {
          enabled: false,
        },
      },
      column: {
        pointInterval: 24 * 3600 * 1000, // one day
      },
    },
  };

  protected featureListWithSeries: TrendingItem[];
  protected datePickerMin: number;
  protected datePickerMax: number;

  protected abstract generateSeriesFromFeatures(
    data: TrendingData,
    features: string[]
  ): Highcharts.SeriesOptionsType[];
  protected abstract createChartOptions(data: TrendingData, features: string[]): Highcharts.Options;

  private navigatorMin: number;
  private navigatorMax: number;
  private redrawRequest: number | null = null;
  constructor(
    protected trendingStore: TrendingStore,
    protected cdr: ChangeDetectorRef,
    protected translateService: TranslateService,
    protected uiService: UIService,
    protected activeErrorsStore: ActiveErrorsStore,
    protected dateTimeService: DateTimeService,
    protected devicesStore: DevicesStore,
    protected installationDetailsStore: InstallationDetailsStore,
    @Inject(TRENDING_UNITS_ADAPTER_IT) protected unitsAdapter: UnitsAdapter
  ) {}

  @HostListener('window:resize') onResize(): void {
    resizeTrendingChart(this.chartOptions, this.chart);
  }

  ngOnInit(): void {
    this.onChartPropertySelected();
    this.initChart();
  }

  ngAfterViewInit(): void {
    this.onDateRangeChange();
    this.subscribeToNavigatorExtremes();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private subscribeToNavigatorExtremes(): void {
    this.trendingStore.navigatorMin$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((min) => {
      this.navigatorMin = min;
      this.updateChartExtremes();
    });

    this.trendingStore.navigatorMax$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((max) => {
      this.navigatorMax = max;
      this.updateChartExtremes();
    });
  }
  // Updated method to set chart extremes based on navigator values
  private updateChartExtremes(): void {
    if (this.navigatorMin !== undefined && this.navigatorMax !== undefined && this.chart) {
      this.chart.xAxis[0].setExtremes(this.navigatorMin, this.navigatorMax);
    }
  }

  getChartInstance(chart: Highcharts.Chart): void {
    this.chart = chart;
  }

  protected initChart(): void {
    Highcharts.setOptions({
      lang: {
        shortMonths: getEnumValues(MONTHS_SHORT).map((month_short) =>
          this.translateService.instant(month_short)
        ),
        shortWeekdays: getEnumValues(DAYS_SHORT).map((day_short) =>
          this.translateService.instant(day_short)
        ),
        weekdays: getEnumValues(WEEKDAYS).map((weekday) => this.translateService.instant(weekday)),
      },
    });
    this.setChartName();
    this.onDataChange();
  }

  private onDataChange(): void {
    combineLatest([
      this.trendingStore.getChartSelectedProperties(this.chartNameKey),
      this.trendingStore.dataSeries$,
      this.devicesStore.selectFeatureForEnergyStorageDataTrending$,
      this.installationDetailsStore.installationTimezone$,
    ])
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(
        ([
          selectedChartProperties,
          data,
          featureForEnergyStorageDataTrending,
          installationTimezone,
        ]) => {
          if (!data) {
            return;
          }

          // Out of all trending features, filter out only these whose don't exist in backend data
          this.trendsFiltered = this.trends
            ? this.trends.filter((trend) =>
                data.series.find((series) => series.feature === trend.feature)
              )
            : [];

          if (!this.trendsFiltered.length) {
            return;
          }

          let features = selectedChartProperties?.features;

          if (!selectedChartProperties) {
            // Construct features for selecting/unselecting behaviour
            const presentChartFeatures = data.series.map((entry) => {
              return entry.feature;
            });
            features = this.trendsFiltered
              .filter((trend) => trend.visible && presentChartFeatures.includes(trend.feature))
              .slice(0, this.maxSelectedProperties)
              .map((trend) => trend.feature);

            // Expand set of features for each device
            const uniqueBoilerSerialsByHeatingType = [];
            const uniqueBoilerSerialsByEnergyStorageType = [];

            data.series.forEach((entry) => {
              if (trendingProperties.includes(entry.feature)) {
                if (!uniqueBoilerSerialsByHeatingType.includes(entry.boilerSerial)) {
                  uniqueBoilerSerialsByHeatingType.push(entry.boilerSerial);
                }
              } else if (featureForEnergyStorageDataTrending.includes(entry.feature)) {
                if (!uniqueBoilerSerialsByEnergyStorageType.includes(entry.boilerSerial)) {
                  uniqueBoilerSerialsByEnergyStorageType.push(entry.boilerSerial);
                }
              }
            });

            const featuresExpandedForMultipleDevicesByHeatingType: string[] = [];
            const featuresExpandedForMultipleDevicesByEnergyStorageType: string[] = [];
            features.forEach((feature) => {
              // By default select feature total otherwise select features only for first device
              if (trendingProperties.includes(feature)) {
                featuresExpandedForMultipleDevicesByHeatingType.push(
                  `${uniqueBoilerSerialsByHeatingType[0]}_${feature}`
                );
              } else if (featureForEnergyStorageDataTrending.includes(feature)) {
                feature.includes('/total')
                  ? featuresExpandedForMultipleDevicesByEnergyStorageType.push(`total_${feature}`)
                  : featuresExpandedForMultipleDevicesByEnergyStorageType.push(
                      `${uniqueBoilerSerialsByEnergyStorageType[0]}_${feature}`
                    );
              }
            });
            features = [
              ...featuresExpandedForMultipleDevicesByHeatingType,
              ...featuresExpandedForMultipleDevicesByEnergyStorageType,
            ];

            this.selectedProperties.setValue(features);
          }

          this.installationTimezone = installationTimezone;
          this.initChartOptions = {
            ...this.initChartOptions,
            time: { timezone: installationTimezone },
          };
          const options = this.createChartOptions(data, features);
          this.chartOptions = options;
          this.updateChartOptionsTooltip();
          this.cdr.markForCheck();

          resizeTrendingChart(options, this.chart);

          this.updateInstallationErrorPlotLines();

          if (this.customLegendElement) {
            this.customLegendElement.instance.redrawLegend();
          }
        }
      );
  }

  protected updateInstallationErrorPlotLines(): void {
    this.activeErrorsStore.activeErrorsTrending$.subscribe((activeErrors) => {
      const errorsGroups = [];
      // Map to helper format
      activeErrors.forEach((error) => {
        const sameDateElement = errorsGroups.find((entry) => entry.date === error.appearedAt);

        const errorText = `${error.errorCode} - ${error.errorDescription}`;

        if (!!sameDateElement) {
          sameDateElement.errors.push(errorText);
        } else {
          errorsGroups.push({
            date: error.appearedAt,
            errors: [errorText],
          });
        }
      });

      // Construct HighCharts objects
      const plotLines = errorsGroups.map((errorGroup) => {
        const plotLine = {
          renderPlotLineInTooltip: false,
          color: '#ff000082',
          zIndex: 1000,
          width: 10,
          value: Date.parse(errorGroup.date),
          date: `${moment(errorGroup.date).format(
            'HH:mm DD.MM.YYYY'
          )} - ${this.translateService.instant('+SEARCH.RESULT_LIST.ACTIVE_ERRORS.LIST_TITLE')}`,
          errors: errorGroup.errors,
          events: null,
        };

        plotLine.events = {
          mouseover: (): void => {
            plotLine.renderPlotLineInTooltip = true;
            // On very small resolution higcharts do not refresh tooltip until a new point is selected
            // so if plot line is in between the two points, we are not able to render it
            // By hiding the tooltip we are forcing highcharts to refresh the tooltip
            // and show new values with data from the plot line
            this.chart.tooltip.hide();
          },
          mouseout: (): void => {
            setTimeout(() => {
              plotLine.renderPlotLineInTooltip = false;
            });
          },
        };

        return plotLine;
      });

      this.chartOptions.xAxis = {
        ...this.chartOptions.xAxis,
        plotLines: plotLines,
      };
    });

    this.cdr.markForCheck();
    resizeTrendingChart(this.chartOptions, this.chart);
  }

  private setChartName(): void {
    const chartNamePrefix = 'chart_';
    // Regex transforms chart name eg. Example Chart Name into Example_Chart_Name
    this.chartNameKey = chartNamePrefix + this.name?.trim().replace(/\s+/g, '_').toLowerCase();
  }

  protected getChartSeries(data: TrendingData, features: string[]): Highcharts.SeriesOptionsType[] {
    const featureList = this.trendsFiltered;
    const featuresPresent = featureList.length;
    const fakeSeriesOptions: Highcharts.SeriesOptionsType[] = [{ type: TrendingChartType.Line }];

    if (featuresPresent) {
      // filter empty features
      this.featureListWithSeries = featureList
        .filter((entry) => {
          const series = data.series.find((set) => set?.feature === entry.feature)?.data;
          const seriesPresent = series?.length;

          return seriesPresent;
        })
        .map((entry) => ({
          ...entry,
          unit: this.unitsAdapter.standardizeUnit(entry.unit),
        }));

      return this.generateSeriesFromFeatures(data, features);
    } else {
      return fakeSeriesOptions;
    }
  }

  protected onSelectChartProperty(boilerSerialfeature: string): void {
    const properties =
      this.selectedProperties.value?.length > 0 ? this.selectedProperties.value : [];

    if (properties.some((entry) => entry.includes(boilerSerialfeature))) {
      const propertiesWithoutEntry = properties.filter((entry) => entry !== boilerSerialfeature);
      this.selectedProperties.setValue(propertiesWithoutEntry);
    } else {
      this.selectedProperties.setValue(properties.concat([boilerSerialfeature]));
    }
  }

  private onChartPropertySelected(): void {
    this.selectedProperties.valueChanges
      .pipe(distinctUntilChanged(isEqual), takeUntil(this.ngUnsubscribe$))
      .subscribe((selectedProperties) => {
        this.trendingStore.selectChartProperties({
          chartName: this.chartNameKey,
          features: [].concat(selectedProperties),
        });
      });
  }

  private onDateRangeChange(): void {
    this.trendingStore.dateRange$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((dateRange: DateRange<Date>) => {
        this.datePickerMin = dateRange.start.valueOf();
        this.datePickerMax = dateRange.end.valueOf();
        updateTrendingChartExtremes(dateRange, this.chart, this.chartOptions);
      });
  }

  private checkSeriesDataGroupingAndSetLabel(): void {
    if (this.chart) {
      this.dataGrouping = this.chart.series.some((series: SeriesWithGroupedData) => {
        return (
          series.userOptions.visible &&
          series.userOptions.id !== 'highcharts-navigator-series' &&
          series.hasGroupedData
        );
      });

      if (this.dataGroupingLabel) {
        this.dataGroupingLabel.destroy();
        this.dataGroupingLabel = undefined;
      }

      if (this.dataGrouping) {
        if (this.uiService.isMobileView()) {
          this.dataGroupingLabel = this.chart.renderer
            .path()
            .attr({
              d:
                'm12 3.0014c1.3202 0 2.525 0.76177 3.316 2.0936l6.0145 10.141c0.8007 1.3474 0.8866 2.7829 0.2385 3.9409-0.6465 1.158-1.9118 1.8219-3.4643 1.8219h-12.209c-1.5525 0-2.816-0.6657-3.4643-1.8219-0.64877-1.1581-0.5609-2.5954 0.23853-3.9428l6.0149-10.143c0.78892-1.328 1.9958-2.0897 3.314-2.0897zm0 1.9998c-0.572 0-1.1424 0.36711-1.5759 1.1015l-6.0423 10.227c-0.869 1.4667-0.19914 2.6697 1.4881 2.6697h12.262c1.6853 0 2.3571-1.2011 1.4881-2.6697l-6.0422-10.227c-0.4332-0.73438-1.0037-1.1015-1.5778-1.1015zm0.9998 9.9986v1.9997h-1.9996v-1.9997zm0-6.9991v5.3995h-1.9996v-5.3995z',
              fill: '#f7ad37',
              zIndex: 1,
            })
            .add();

          this.dataGroupingLabel.on('click', () => {
            const isVisible = this.displayTooltip$.getValue();
            this.displayTooltip$.next(!isVisible);
          });
        } else {
          const labelWidth = this.chart.chartWidth - 90;
          this.dataGroupingLabel = this.chart.renderer
            .label(
              this.translateService.instant(
                '+INSTALLATIONS.INSTALLATION.TRENDING.CHARTS.DATA_AGGREGATED_WARNING_LABEL'
              ),
              40,
              0
            )
            .css({
              width: labelWidth,
              height: this.chart.chartHeight,
              lineHeight: '18px',
            })
            .attr({
              fill: '#f7ad37',
              zIndex: 5,
              useHTML: true,
            })
            .add();
        }
      }
    }
  }

  // Handle AfterSetExtremes event for navigator
  private onAfterSetExtremes(event: Highcharts.AxisSetExtremesEventObject): void {
    const { min, max, trigger } = event;
    // Ensure we're only reacting to user interactions and not programmatic changes
    if (trigger !== 'navigator') {
      return;
    }

    this.chart.xAxis[0].setExtremes(min, max);
    this.trendingStore.setNavigatorExtremes(min, max);
    // Use requestAnimationFrame to schedule the redraw
    if (this.redrawRequest) {
      cancelAnimationFrame(this.redrawRequest);
    }

    this.redrawRequest = requestAnimationFrame(() => {
      this.chart.redraw();
    });
  }

  protected updateChartOptionsTooltip(): void {
    const locale = this.dateTimeService.getLocale();
    const format = this.dateTimeService.getFormat();
    const timezone = this.installationTimezone;

    this.chartOptions = {
      ...this.chartOptions,
      tooltip: {
        ...initBaseTrendingChartOptions.tooltip,
        formatter: function (context): string {
          return showChartTooltip(context, this, true, true, locale, format, timezone);
        },
      },
    };
  }
}
